/* eslint-disable react/jsx-no-bind */
import { startTransition, useCallback, useRef, useState } from "react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "app/components/shared/Command";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "app/components/shared/PopoverNext";

import { StepSearchInput } from "./StepSearchInput";
import { StepSearchItem } from "./StepSearchItem";
import { useStepSearch } from "./useStepSearch";
import { useHotkeys } from "react-hotkeys-hook";
import { Key } from "ts-key-enum";

export function StepSearch() {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { steps, selectStep } = useStepSearch();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useHotkeys("s", () => inputRef.current?.focus(), {
    preventDefault: true,
  });

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = event.currentTarget.value;

    startTransition(() => {
      setOpen(Boolean(currentValue));
      setSearch(currentValue);

      // Wait for the next frame to ensure the command list is rendered
      // before scrolling to the first item.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document
            .querySelector("[cmdk-item][aria-selected=true]")
            ?.scrollIntoView({ block: "nearest" });
        });
      });
    });
  }, []);

  return (
    <Command loop={true} className="gap-0 p-0">
      <CommandInput value={search} className="hidden" />

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger disabled={true}>
          <StepSearchInput
            ref={inputRef}
            onChange={handleChange}
            onFocus={() => {
              if (inputRef.current) {
                inputRef.current.select();
                setOpen(Boolean(inputRef.current?.value));
              }
            }}
            onKeyDown={(event) => {
              if (event.key === Key.Escape) {
                if (inputRef.current?.value) {
                  inputRef.current.value = "";
                } else {
                  inputRef.current?.blur();
                }
              }
            }}
          />
        </PopoverTrigger>

        <PopoverPortal>
          <PopoverContent
            arrow={false}
            onOpenAutoFocus={(event) => event.preventDefault()}
            data-testid="step-search-results"
            className="mt-2 w-[var(--radix-popper-anchor-width)] p-0 data-[state=closed]:duration-0"
          >
            <CommandList className="max-h-[min(var(--radix-popover-content-available-height),32rem)] scroll-py-2 p-2">
              <CommandEmpty>No matching steps</CommandEmpty>

              <CommandGroup>
                {steps.map((step) => (
                  <StepSearchItem key={step.uuid} onSelect={selectStep} step={step} />
                ))}
              </CommandGroup>
            </CommandList>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    </Command>
  );
}
